import * as React from "react";
import { FiChevronRight, FiStar, FiCamera, FiHeart, FiShare2, FiShoppingCart, FiSend } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const StarRating = ({ rating, className }) => (
  <div className={cn("flex items-center gap-0.5", className)}>
    {[...Array(5)].map((_, i) => (
      <FiStar
        key={i}
        className={cn(
          "h-4 w-4",
          i < Math.floor(rating) ? "text-brand-gold fill-brand-gold" : "text-brand-darkSage/20"
        )}
      />
    ))}
    <span className="ml-2 text-sm font-medium text-brand-lightText">{rating.toFixed(1)}</span>
  </div>
);

export const ProductDetailPage = ({
  product,
  seller,
  breadcrumbs,
  onAddToCart
}) => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isFavorited, setIsFavorited] = React.useState(false);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} on Ceylanthro!`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleInquire = () => {
    const message = encodeURIComponent(`Hi, I would like to inquire about: ${product.name}`);
    window.open(`https://wa.me/94774468660?text=${message}`, '_blank');
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8 bg-white/50 backdrop-blur-sm rounded-3xl border border-white/40 shadow-xl">
      {/* Breadcrumbs Navigation */}
      <nav aria-label="Breadcrumb" className="flex items-center text-sm text-brand-lightText mb-6">
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            <Link to={item.href} className="hover:text-brand-gold transition-colors">{item.label}</Link>
            {index < breadcrumbs.length - 1 && <FiChevronRight className="h-4 w-4 mx-1" />}
          </React.Fragment>
        ))}
      </nav>

      <div className="flex justify-between items-center mb-6">
        <div />
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="hover:text-brand-gold" onClick={() => setIsFavorited(!isFavorited)}>
            <FiHeart className={cn("h-5 w-5", isFavorited && "fill-brand-gold text-brand-gold")} />
            <span className="sr-only">Favorite</span>
          </Button>
          <Button variant="ghost" size="icon" className="hover:text-brand-gold" onClick={handleShare}>
            <FiShare2 className="h-5 w-5" />
            <span className="sr-only">Share</span>
          </Button>
        </div>
      </div>

      {/* Main content grid */}
      <main className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16">
        {/* Image Gallery Section */}
        <div className="flex flex-col gap-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 0.4 }}
              className="relative aspect-square w-full flex items-center justify-center bg-brand-darkSand/20 rounded-2xl border border-white/50 p-8"
            >
              <img
                src={product.images[currentImageIndex]}
                alt={`${product.name} image ${currentImageIndex + 1}`}
                className="object-contain w-full h-full filter drop-shadow-2xl"
              />
            </motion.div>
          </AnimatePresence>

          {product.images.length > 1 && (
            <div className="flex items-center justify-between px-2">
              <div className="flex gap-3">
                {product.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={cn(
                      "h-2.5 w-2.5 rounded-full transition-all duration-300",
                      currentImageIndex === index ? "bg-brand-gold w-6" : "bg-brand-darkSage/30 hover:bg-brand-darkSage/50"
                    )}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Product Details Section */}
        <div className="flex flex-col justify-center">
          <h1 className="text-3xl md:text-5xl font-serif text-brand-darkSage leading-tight">{product.name}</h1>
          
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-3xl font-sans font-bold text-brand-darkSage">{product.currency}{product.price.toFixed(2)}</span>
            <span className="text-sm text-brand-lightText font-medium">+shipping</span>
          </div>

          <div className="flex gap-4 my-8">
            <Button onClick={onAddToCart} size="lg" className="flex-1 gap-2 bg-brand-darkSage hover:bg-brand-gold text-white rounded-xl h-14 text-lg transition-all duration-300 shadow-xl">
              <FiShoppingCart className="h-5 w-5"/>
              Add to Cart
            </Button>
            <Button size="lg" variant="outline" onClick={handleInquire} className="flex-1 gap-2 border-brand-darkSage text-brand-darkSage hover:bg-brand-sand rounded-xl h-14 text-lg">
              <FiSend className="h-5 w-5"/>
              Inquire
            </Button>
          </div>

          {/* Tags/Badges */}
          <div className="flex flex-wrap gap-2 mb-8">
            {product.tags.map((tag, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="text-sm font-medium py-1.5 px-4 gap-2 bg-brand-darkSage/5 text-brand-darkSage border-none"
              >
                {tag.icon && <tag.icon className="h-4 w-4 text-brand-gold" />}
                {tag.label}
              </Badge>
            ))}
          </div>

          {/* Description */}
          <div className="space-y-4">
            <h3 className="font-serif text-xl text-brand-darkSage">Description</h3>
            <p className="text-brand-lightText leading-relaxed text-lg font-light">
              {product.description}
            </p>
          </div>

          {/* Seller Information */}
          <div className="mt-12 pt-8 border-t border-brand-darkSage/10">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14 border-2 border-white shadow-md">
                  <AvatarImage src={seller.avatarUrl} alt={seller.name} />
                  <AvatarFallback className="bg-brand-sand text-brand-darkSage font-serif text-xl">{seller.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-serif font-bold text-brand-darkSage text-lg">{seller.name}</p>
                  <StarRating rating={seller.rating} />
                </div>
              </div>
              <Button variant="link" className="text-brand-gold hover:text-brand-darkSage font-semibold tracking-wider uppercase text-xs">
                View Brand &rarr;
              </Button>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};